import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

import { Grouping, GroupingAPI, GroupingsAPI, GroupingSplit, GroupingTypes } from './Grouping';
import SummarizedReportGroupItem from './SummarizedReportGroupItem';
import SummarizedReportOthers from './SummarizedReportOthers';
import SummarizedReportSite from './SummarizedReportSite';
import SummarizedReportUnit from './SummarizedReportUnit';

const GROUP_NAMES: Record<GroupingTypes, string> = {
  [GroupingTypes.UNIQUE_ADVERTISER]: 'names.advertiser',
  [GroupingTypes.BUYER]: 'names.programmaticBuyer',
  [GroupingTypes.DEAL]: 'names.programmaticDeal',
  [GroupingTypes.DSP]: 'names.buyingPlatform',
  [GroupingTypes.CAMPAIGN]: 'names.campaign',
  [GroupingTypes.LINE_ITEM]: 'names.lineItem',
  [GroupingTypes.MEDIA_OWNER]: 'names.mediaOwner',
  [GroupingTypes.ACCOUNT]: 'names.account',
  [GroupingTypes.MEDIA_TYPE]: 'names.venueType',
  [GroupingTypes.NETWORK]: 'names.network',
};

const ENTITY_LINK_ROUTES: Partial<Record<GroupingTypes, string>> = {
  [GroupingTypes.DEAL]: '/deals/',
  [GroupingTypes.CAMPAIGN]: '/campaign/',
};

export default class SummarizedReport extends Validatable {
  public groupings: Grouping[];

  constructor(
    public resolution: string,
    public from: string,
    public to: string,
    public impressions: number[],
    public revenue: number[],
    public avgCPM: number[],
    public byUnit,
    public bySite,
    public byUnitOthers,
    public bySiteOthers,
    groupings: GroupingsAPI,
    public oldestUnsummarizedTimeUtc,
  ) {
    super();
    this.groupings = SummarizedReport.processGroupings(groupings);

    if (this.byUnit !== null && this.bySite !== null) {
      this.processStats();
    }
  }

  get isEmpty() {
    return this.impressions?.length === 0;
  }

  private processStats() {
    this.impressions = [];
    this.revenue = [];
    this.avgCPM = [];

    if (this.byUnit.length > 0) {
      const emptyArray = this.byUnit[Object.keys(this.byUnit)[0]].impressions.map(() => 0);
      this.impressions = [...emptyArray];
      this.revenue = [...emptyArray];
      this.avgCPM = [...emptyArray];
      this.byUnit.forEach((u) => {
        u.impressions.forEach((x, i) => {
          this.impressions[i] += x;
        });
        u.revenue.forEach((x, i) => {
          this.revenue[i] += x;
        });
        u.cpm.forEach((x, i) => {
          this.avgCPM[i] += x / this.byUnit.length;
        });
      });

      if (this.byUnitOthers !== null) {
        this.byUnitOthers.impressions.forEach((x, i) => {
          this.impressions[i] += x;
        });
        this.byUnitOthers.revenue.forEach((x, i) => {
          this.revenue[i] += x;
        });
        this.byUnitOthers.cpm.forEach((x, i) => {
          this.avgCPM[i] += x / this.byUnitOthers.count;
        });
      }
    }
  }

  private static processGroupings(groupings: GroupingsAPI): Grouping[] {
    return Object.entries(groupings).map(([key, grouping]) => {
      const groupName: string = capitalize(i18n.global.t(GROUP_NAMES[key], 2));
      const entityLinkRoute: string = ENTITY_LINK_ROUTES[key];
      const creativeIDs =
        key === GroupingTypes.CAMPAIGN
          ? Array.from(
              new Set([
                ...grouping.TOP_IMPRESSIONS.map((x) => x.creative_ids).flat(),
                ...grouping.TOP_SPEND.map((x) => x.creative_ids).flat(),
                ...grouping.TOP_PAID_CPM.map((x) => x.creative_ids).flat(),
              ]),
            )
          : null;

      const byImpressions = grouping.TOP_IMPRESSIONS.map((x) => new SummarizedReportGroupItem(x)).sort(
        (a, b) => b.impressions - a.impressions,
      );
      const byRevenue = grouping.TOP_SPEND.map((x) => new SummarizedReportGroupItem(x)).sort(
        (a, b) => b.revenue - a.revenue,
      );
      const byPaidCPM =
        grouping.TOP_PAID_CPM != null
          ? grouping.TOP_PAID_CPM.map((x) => new SummarizedReportGroupItem(x)).sort((a, b) => b.cpm - a.cpm)
          : byImpressions
              .map(
                (x, i) =>
                  new SummarizedReportGroupItem({
                    id: x.id,
                    sspId: x.sspId,
                    name: x.name,
                    impressions: x.impressions,
                    revenue: byRevenue[i].revenue,
                    cpm: byRevenue[i].revenue / (x.impressions / 1000),
                  }),
              )
              .sort((a, b) => b.cpm - a.cpm);

      return {
        id: key,
        groupName,
        entityLinkRoute,
        byImpressions,
        byRevenue,
        byPaidCPM,
        creativeIDs,
        split: SummarizedReport.processGroupingSplit(grouping),
      };
    });
  }

  private static processGroupingSplit(grouping: GroupingAPI): GroupingSplit[] {
    if (grouping.SPLIT_FOR_AD_SERVER == null) {
      return null;
    }

    return [
      {
        id: 'AD_SERVER',
        label: capitalize(i18n.global.t('names.direct', { n: 1 })), // adServer rename direct by https://hivestack.atlassian.net/browse/BRO-1978
        cpm: grouping.SPLIT_FOR_AD_SERVER.cpm,
        impressions: grouping.SPLIT_FOR_AD_SERVER.impressions,
        revenue: grouping.SPLIT_FOR_AD_SERVER.revenue,
        revenuePercent: grouping.SPLIT_FOR_AD_SERVER.revenue_percent,
      },
      {
        id: 'EXCHANGE',
        label: capitalize(i18n.global.t('names.adExchange', { n: 1 })),
        cpm: grouping.SPLIT_FOR_EXCHANGE.cpm,
        impressions: grouping.SPLIT_FOR_EXCHANGE.impressions,
        revenue: grouping.SPLIT_FOR_EXCHANGE.revenue,
        revenuePercent: grouping.SPLIT_FOR_EXCHANGE.revenue_percent,
      },
      {
        id: 'SSP',
        label: `${window.location.host.includes('liveboard') ? '' : 'Hivestack '}${capitalize(i18n.global.t('names.ssp', { n: 1 }))}`,
        cpm: grouping.SPLIT_FOR_SSP.cpm,
        impressions: grouping.SPLIT_FOR_SSP.impressions,
        revenue: grouping.SPLIT_FOR_SSP.revenue,
        revenuePercent: grouping.SPLIT_FOR_SSP.revenue_percent,
      },
      ...(grouping.SPLIT_FOR_YIELD_MANAGER != null
        ? grouping.SPLIT_FOR_YIELD_MANAGER.map((x) => ({
            id: x.name,
            label: x.name,
            cpm: x.cpm,
            impressions: x.impressions,
            revenue: x.revenue,
            revenuePercent: x.revenue_percent,
          }))
        : []),
    ];
  }

  static fromApi(apiObject) {
    return new SummarizedReport(
      apiObject.resolution,
      apiObject.from,
      apiObject.to,
      apiObject.impressions,
      apiObject.revenue,
      apiObject.cpm_paid,
      apiObject.by_unit !== undefined
        ? Object.keys(apiObject.by_unit).map((x) => {
            return SummarizedReportUnit.fromApi({ id: x, ...apiObject.by_unit[x] });
          })
        : null,
      apiObject.by_site !== undefined
        ? Object.keys(apiObject.by_site).map((x) => {
            return SummarizedReportSite.fromApi({ id: x, ...apiObject.by_site[x] });
          })
        : null,
      apiObject.by_unit_others !== undefined ? SummarizedReportOthers.fromApi(apiObject.by_unit_others) : null,
      apiObject.by_site_others !== undefined ? SummarizedReportOthers.fromApi(apiObject.by_site_others) : null,
      apiObject.groupings !== undefined ? apiObject.groupings : null,
      apiObject.oldest_unsummarized_time_utc != null
        ? DateTime.fromISO(apiObject.oldest_unsummarized_time_utc, { zone: 'utc' })
        : null,
    );
  }

  static validatorCreateRules() {
    return {};
  }
}
